/**
 * QueueOfferIdentityTrigger + QueueFirstJoinBlock — the queue-join panel's
 * first-join affordance (CU-86cb7ewja). Extracted from `queue-join-panel.tsx`
 * (max-lines split); see that file's own doc comment for the full picture.
 */
import { useState } from 'react';
import { UserPlus, X } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { IconButtonWithTooltip } from '@ooh/ui/components/icon-button-with-tooltip';
import { QueueIdentityFields } from './queue-identity-fields';
import {
  buildQueueJoinPayload,
  type QueueJoinClientOption,
  type QueueJoinIdentity,
  type QueueJoinOfferOption,
  type QueueJoinPanelLabels,
  type QueueJoinPayload,
} from './queue-join-types';

/** Visible, editable offer-identity summary — "Zmień" is the only way to change it. */
export function QueueOfferIdentityTrigger({
  testId,
  label,
  changeLabel,
  onEdit,
}: {
  testId: string;
  label: string;
  changeLabel: string;
  onEdit: () => void;
}) {
  return (
    <div
      className="flex items-center justify-between gap-2 rounded-md border border-border/60 bg-muted/20 px-2.5 py-1.5"
      data-testid={`${testId}-identity-trigger`}
    >
      <span className="truncate text-xs font-medium">{label}</span>
      <Button
        type="button"
        size="xs"
        variant="ghost"
        data-testid={`${testId}-identity-edit-btn`}
        onClick={onEdit}
      >
        {changeLabel}
      </Button>
    </div>
  );
}

/**
 * First-join case (no own entries yet): submits the offer identity as-is
 * when the trigger is still collapsed (untouched), or the picked
 * client/brand once the caller opens and uses the identity fields — NEVER
 * both in the same request. Extracted from `QueueJoinPanel` to keep that
 * component's branching within the complexity budget.
 */
export function QueueFirstJoinBlock({
  testId,
  identity,
  clientOptions,
  offerOptions,
  labels,
  isJoinPending,
  ttlDateStr,
  onJoin,
}: {
  testId: string;
  identity: QueueJoinIdentity;
  clientOptions: QueueJoinClientOption[];
  offerOptions: QueueJoinOfferOption[];
  labels: QueueJoinPanelLabels;
  isJoinPending?: boolean;
  ttlDateStr: string;
  onJoin: (payload: QueueJoinPayload) => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [clientId, setClientId] = useState('');
  const [brand, setBrand] = useState('');
  const [offerId, setOfferId] = useState('');

  function resetForm() {
    setShowForm(false);
    setClientId('');
    setBrand('');
    setOfferId('');
  }

  function submitFirstJoin() {
    if (identity.mode === 'offer' && !showForm) {
      onJoin({ offerId: identity.offerId, ttlExpiresAt: ttlDateStr });
      return;
    }
    const payload = buildQueueJoinPayload(clientId, brand, offerId, ttlDateStr);
    if (!payload) return;
    onJoin(payload);
    resetForm();
  }

  // Disabled until a client is picked, EXCEPT the untouched offer trigger, which already
  // has a valid identity to submit.
  const disabled =
    identity.mode === 'offer' && !showForm
      ? Boolean(isJoinPending)
      : !clientId || Boolean(isJoinPending);

  return (
    <div className="space-y-2" data-testid={`${testId}-first-join`}>
      {identity.mode === 'offer' && !showForm ? (
        <QueueOfferIdentityTrigger
          testId={testId}
          label={identity.label}
          changeLabel={labels.changeIdentityLabel}
          onEdit={() => setShowForm(true)}
        />
      ) : (
        <>
          {identity.mode === 'offer' ? (
            <div className="flex items-center justify-end">
              <IconButtonWithTooltip
                icon={X}
                tooltip={labels.cancelLabel}
                variant="ghost"
                size="icon-sm"
                onClick={resetForm}
              />
            </div>
          ) : null}
          <QueueIdentityFields
            testId={testId}
            clientOptions={clientOptions}
            offerOptions={offerOptions}
            clientId={clientId}
            brand={brand}
            offerId={offerId}
            onClientChange={setClientId}
            onBrandChange={setBrand}
            onOfferChange={setOfferId}
            labels={labels}
          />
        </>
      )}
      <Button
        size="xs"
        data-testid={`${testId}-join-btn`}
        disabled={disabled}
        onClick={submitFirstJoin}
      >
        <UserPlus className="h-3.5 w-3.5" />
        {labels.joinLabel}
      </Button>
    </div>
  );
}
